import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  amountSchema,
  type ContactSummary,
  type EstimateDetail,
  modelYearSchema,
  odometerSchema,
  ownerCountSchema,
  type SetTemplateReferencePriceOutput,
  type TemplateSummary,
  type TemplateVersion,
  type UpdateEstimateInput,
  VIN_MAX_LENGTH,
  vinSchema,
} from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { CatalogSelect } from '../../components/ui/CatalogSelect';
import { Checkbox } from '../../components/ui/Checkbox';
import { FormField, FormSection } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { formatMoney } from '../../lib/format';
import { fitsSchema, serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { makesOf, modelsOf, TRIMS, trimOptions } from '../../lib/vehicle-catalog';
import { FUELS, ORIGINS, SERVICE_HISTORIES, toOptionalNumber, toOptionalText, toTriState, USAGES } from './estimates-display';
import type { EstimatorForm } from './estimator-form';
import { QuickRangeCard } from './QuickRangeCard';
import type { LocalQuickRange, LocalValuation } from './valuation-local';

type FactsPatch = Omit<UpdateEstimateInput, 'estimateId' | 'expectedRevision'>;

/**
 * The typed numbers, each with the **contract's own** schema (plan §2.5).
 *
 * Every one of them is reached through values the service refuses: `2026` passes through `2`, `20` and
 * `202`, an odometer can be typed with a decimal point, and a price can gain a digit. The autosave used
 * to queue those on a 400 ms debounce and re-send them for as long as the page was open, which is what
 * the production log's "Input validation failed" lines were.
 */
const NUMBER_FIELD_SCHEMAS = {
  modelYear: modelYearSchema,
  odometerKm: odometerSchema,
  ownerCount: ownerCountSchema,
  customerAskingPrice: amountSchema,
  referencePrice: amountSchema,
  registrationTransferFee: amountSchema,
} as const;

type NumberField = keyof typeof NUMBER_FIELD_SCHEMAS;

/** How long the mobile number has to stand still before the workspace is asked who owns it. */
export const PHONE_LOOKUP_DELAY_MS = 800;

/** Fewer digits than this is still being typed, and matching it would find a stranger (plan §11.19). */
const PHONE_MIN_DIGITS = 6;

const digitsOf = (phone: string): string => phone.replace(/\D/g, '');

/** The same person as far as the prompt is concerned: the name typed matches the owner's, ignoring case. */
const sameName = (left: string, right: string): boolean => left.trim().toLowerCase() === right.trim().toLowerCase();

type PhoneCheck = { status: 'idle' } | { status: 'checking' } | { status: 'prompt'; contact: ContactSummary };

export interface EstimatorStep1Props {
  detail: EstimateDetail;
  form: EstimatorForm;
  local: { valuation: LocalValuation; quickRange: LocalQuickRange } | null;
  templates: readonly TemplateSummary[];
  templateValues: TemplateVersion | null;
  onChange: (patch: Partial<EstimatorForm>, save: FactsPatch | null) => void;
  onPickTemplate: (templateId: string) => void;
  onSelectScenario: (key: string | null) => void;
  /** Step 1's required fields that are still empty, so each can say so under itself once it was touched. */
  missingFields?: readonly string[];
}

/**
 * Step 1 — customer and vehicle identity (plan §7), one field per row (business review 2026-09-23).
 *
 * The customer is name and mobile. The mobile number is **not** matched while it is being typed: a
 * partial number used to match a stranger's and replace the name on the screen. Now the workspace is
 * asked once the number stands still (or the field is left), and an existing owner is only ever offered —
 * "Did you mean …?" — never adopted without the salesperson saying so (plan §11.19).
 *
 * The template Select never applies itself — it opens the preview Dialog, because replacing the shared
 * values silently would change a price the customer has already been told (PP AC09).
 */
export const EstimatorStep1 = ({
  detail,
  form,
  local,
  templates,
  templateValues,
  onChange,
  onPickTemplate,
  onSelectScenario,
  missingFields = [],
}: EstimatorStep1Props) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  /** Which typed numbers the contract would refuse: shown on the field, and never queued. */
  const [outOfRange, setOutOfRange] = useState<Partial<Record<NumberField, boolean>>>({});
  /** Fields the salesperson has been in and out of, so an empty required one says so only after that. */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [phoneCheck, setPhoneCheck] = useState<PhoneCheck>({ status: 'idle' });
  const [alsoUpdateTemplate, setAlsoUpdateTemplate] = useState(false);
  const [templateUpdate, setTemplateUpdate] = useState<SetTemplateReferencePriceOutput | null>(null);
  const [templateUpdateError, setTemplateUpdateError] = useState<string | null>(null);

  const setReferencePrice = useMutation(orpc.templates.setReferencePrice.mutationOptions());

  /** The latest name and number, for a lookup that answers after the person has typed on. */
  const latest = useRef({ name: form.customerName, phone: form.customerPhone });
  /** The number for which the salesperson answered "Keep what I typed", so it is not asked twice. */
  const keptPhone = useRef<string | null>(null);
  const phoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    latest.current = { name: form.customerName, phone: form.customerPhone };
  }, [form.customerName, form.customerPhone]);

  useEffect(
    () => () => {
      if (phoneTimer.current !== null) {
        clearTimeout(phoneTimer.current);
      }
    },
    [],
  );

  const vinQuery = useQuery(
    orpc.estimates.checkVin.queryOptions({
      input: { vin: form.vin.trim(), excludeEstimateId: detail.id },
      enabled: form.vin.trim().length > 3,
    }),
  );

  const vinMatches = vinQuery.data?.estimates ?? [];

  const publishedTemplates = useMemo(() => templates.filter((template) => template.publishedVersionId !== null), [templates]);

  const isMissing = (field: string): boolean => missingFields.includes(field) && touched[field] === true;
  const requiredError = (field: string): string | undefined => (isMissing(field) ? t('estimates.requiredField') : undefined);
  const touch = (field: string) => () => setTouched((current) => ({ ...current, [field]: true }));

  // ── the customer ────────────────────────────────────────────────────────────────────────────────
  /**
   * Asks the workspace who owns the number, once, and decides what to save. Nothing is linked to an
   * existing contact unless its name is the one typed — or the salesperson says "Use this customer".
   */
  const resolveCustomer = async (): Promise<void> => {
    const { phone } = latest.current;

    if (digitsOf(phone).length < PHONE_MIN_DIGITS) {
      return;
    }

    setPhoneCheck({ status: 'checking' });

    let match: ContactSummary | null = null;

    try {
      match = await queryClient.fetchQuery(orpc.contacts.findByPhone.queryOptions({ input: { phone: phone.trim() } }));
    } catch {
      // A lookup that failed is not a reason to lose the customer: the save below still carries it.
      match = null;
    }

    // The person typed on while the question was out: this answer is about a number that is gone.
    if (latest.current.phone !== phone) {
      return;
    }

    const typedName = latest.current.name;

    if (match === null) {
      setPhoneCheck({ status: 'idle' });

      if (typedName.trim() !== '') {
        onChange({}, { customer: { name: typedName.trim(), phone: phone.trim() } });
      }

      return;
    }

    if (typedName.trim() !== '' && sameName(match.name, typedName)) {
      setPhoneCheck({ status: 'idle' });

      if (match.id !== detail.customerContactId) {
        onChange({}, { customerContactId: match.id });
      }

      return;
    }

    if (keptPhone.current === digitsOf(phone)) {
      setPhoneCheck({ status: 'idle' });

      if (typedName.trim() !== '') {
        onChange({}, { customer: { name: typedName.trim(), phone: phone.trim(), matchExisting: false } });
      }

      return;
    }

    setPhoneCheck({ status: 'prompt', contact: match });
  };

  const schedulePhoneLookup = (delayMs: number): void => {
    if (phoneTimer.current !== null) {
      clearTimeout(phoneTimer.current);
    }

    phoneTimer.current = setTimeout(() => {
      phoneTimer.current = null;
      void resolveCustomer();
    }, delayMs);
  };

  const adoptExistingCustomer = (contact: ContactSummary): void => {
    keptPhone.current = null;
    setPhoneCheck({ status: 'idle' });
    onChange({ customerName: contact.name, customerPhone: contact.phone }, { customerContactId: contact.id });
  };

  const keepTypedCustomer = (): void => {
    const { name, phone } = latest.current;

    keptPhone.current = digitsOf(phone);
    setPhoneCheck({ status: 'idle' });

    if (name.trim() !== '') {
      onChange({}, { customer: { name: name.trim(), phone: phone.trim(), matchExisting: false } });
    }
  };

  // ── typed values ────────────────────────────────────────────────────────────────────────────────
  /**
   * The keystroke always reaches the form — the person is still typing — but a value the contract would
   * reject is **not queued**: the field says so instead, and the save happens as soon as what is typed
   * is a number the service can take.
   */
  const onNumberChange = (key: NumberField, typed: string) => {
    const value = toOptionalNumber(typed);
    const accepted = fitsSchema(NUMBER_FIELD_SCHEMAS[key], value);

    setOutOfRange((current) => ({ ...current, [key]: !accepted }));
    onChange({ [key]: typed }, accepted ? { [key]: value } : null);
  };

  const numberField = (key: NumberField, label: string, options: { hint?: string; required?: boolean } = {}) => (
    <FormField
      id={`estimate-${key}`}
      label={label}
      hint={options.hint}
      required={options.required}
      error={outOfRange[key] ? t('estimates.numberOutOfRange') : requiredError(key)}
    >
      <Input
        id={`estimate-${key}`}
        type="number"
        dir="ltr"
        min={0}
        className="tabular-nums"
        value={form[key]}
        hasError={Boolean(outOfRange[key]) || isMissing(key)}
        onBlur={touch(key)}
        onChange={(event) => onNumberChange(key, event.target.value)}
      />
    </FormField>
  );

  const triStateField = (key: 'faceliftApplies' | 'customsApplies', label: string, hint: string) => (
    <FormField
      id={`estimate-${key}`}
      label={label}
      hint={hint}
    >
      <Select
        id={`estimate-${key}`}
        value={form[key]}
        onChange={(event) => onChange({ [key]: event.target.value }, { [key]: toTriState(event.target.value) })}
        options={[
          { value: '', label: t('estimates.notAnswered') },
          { value: 'false', label: t('common.no') },
          { value: 'true', label: t('common.yes') },
        ]}
      />
    </FormField>
  );

  // ── the last price, and the template it came from ───────────────────────────────────────────────
  const typedReferencePrice = toOptionalNumber(form.referencePrice);
  const templateReferencePrice = templateValues?.referencePriceDefault ?? null;
  const canUpdateTemplate =
    detail.templateId !== null && typedReferencePrice !== null && typedReferencePrice > 0 && typedReferencePrice !== templateReferencePrice;

  const sendReferencePriceToTemplate = async (): Promise<void> => {
    if (detail.templateId === null || typedReferencePrice === null) {
      return;
    }

    setTemplateUpdateError(null);

    try {
      const result = await setReferencePrice.mutateAsync({
        templateId: detail.templateId,
        referencePrice: typedReferencePrice,
        estimateId: detail.id,
      });

      setTemplateUpdate(result);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.templates.get.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.templates.list.key() }),
      ]);
    } catch (error: unknown) {
      setAlsoUpdateTemplate(false);
      setTemplateUpdateError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  };

  /** The template's own registration and transfer figures, so the salesperson sees what they replace. */
  const templateFees = useMemo(() => {
    const trace = local?.valuation.run.trace ?? {};
    const registration = trace.registration?.cost?.estimate;
    const transfer = trace.transfer?.cost?.estimate;

    return registration === undefined && transfer === undefined ? null : (registration ?? 0) + (transfer ?? 0);
  }, [local]);

  const vinTooLong = !vinSchema.safeParse(form.vin).success;

  const makes = useMemo(() => makesOf(templates), [templates]);
  const models = useMemo(() => modelsOf(templates, form.make), [form.make, templates]);
  const trimValue = TRIMS.find((trim) => sameName(trim, form.trim)) ?? form.trim;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,42rem)_320px]">
        <div className="space-y-6 rounded-lg border border-border bg-card p-5">
          <FormSection title={t('estimates.customerSection')}>
            <div className="space-y-4">
              <FormField
                id="estimate-customer-name"
                label={t('estimates.customerName')}
              >
                <Input
                  id="estimate-customer-name"
                  value={form.customerName}
                  onChange={(event) => onChange({ customerName: event.target.value }, null)}
                  onBlur={() => {
                    if (phoneCheck.status !== 'prompt') {
                      void resolveCustomer();
                    }
                  }}
                />
              </FormField>
              <FormField
                id="estimate-customer-phone"
                label={t('estimates.customerPhone')}
                hint={t('estimates.customerPhoneHint')}
              >
                <Input
                  id="estimate-customer-phone"
                  type="tel"
                  dir="ltr"
                  value={form.customerPhone}
                  onChange={(event) => {
                    setPhoneCheck({ status: 'idle' });
                    latest.current = { ...latest.current, phone: event.target.value };
                    onChange({ customerPhone: event.target.value }, null);
                    schedulePhoneLookup(PHONE_LOOKUP_DELAY_MS);
                  }}
                  onBlur={() => {
                    if (phoneTimer.current !== null) {
                      clearTimeout(phoneTimer.current);
                      phoneTimer.current = null;
                      void resolveCustomer();
                    }
                  }}
                />
              </FormField>
              {phoneCheck.status === 'prompt' ? (
                <div
                  role="alert"
                  className="space-y-2 rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-3 text-sm text-[#a16207]"
                >
                  <p>
                    {t('estimates.didYouMean', { name: phoneCheck.contact.name })}{' '}
                    <span
                      dir="ltr"
                      className="font-mono"
                    >
                      ({phoneCheck.contact.phone})
                    </span>
                    {t('estimates.didYouMeanEnd')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => adoptExistingCustomer(phoneCheck.contact)}
                    >
                      {t('estimates.useThisCustomer')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={keepTypedCustomer}
                    >
                      {t('estimates.keepWhatITyped')}
                    </Button>
                  </div>
                </div>
              ) : null}
              {numberField('customerAskingPrice', t('estimates.customerAskingPrice'), { hint: t('estimates.customerAskingPriceHint') })}
            </div>
          </FormSection>

          <FormSection title={t('estimates.identitySection')}>
            <div className="space-y-4">
              <FormField
                id="estimate-template"
                label={t('estimates.template')}
                hint={t('estimates.templateHint')}
                required
                error={requiredError('templateId')}
              >
                <Select
                  id="estimate-template"
                  value={detail.templateId ?? ''}
                  placeholder={t('estimates.templatePlaceholder')}
                  hasError={isMissing('templateId')}
                  onBlur={touch('templateId')}
                  onChange={(event) => {
                    if (event.target.value !== '' && event.target.value !== detail.templateId) {
                      onPickTemplate(event.target.value);
                    }
                  }}
                  options={publishedTemplates.map((template) => ({
                    value: template.id,
                    label: `${template.name} · v${template.publishedVersion ?? 1}`,
                  }))}
                />
              </FormField>
              <FormField
                id="estimate-make"
                label={t('estimates.make')}
                required
                error={requiredError('make')}
              >
                <CatalogSelect
                  id="estimate-make"
                  value={form.make}
                  options={makes}
                  placeholder={t('catalog.chooseMake')}
                  otherLabel={t('catalog.otherMake')}
                  otherPlaceholder={t('catalog.typeMake')}
                  hasError={isMissing('make')}
                  onChange={(value) => {
                    touch('make')();
                    onChange({ make: value }, { make: toOptionalText(value) });
                  }}
                />
              </FormField>
              <FormField
                id="estimate-model"
                label={t('estimates.model')}
                required
                error={requiredError('model')}
              >
                <CatalogSelect
                  id="estimate-model"
                  value={form.model}
                  options={models}
                  placeholder={t('catalog.chooseModel')}
                  otherLabel={t('catalog.otherModel')}
                  otherPlaceholder={t('catalog.typeModel')}
                  hasError={isMissing('model')}
                  onChange={(value) => {
                    touch('model')();
                    onChange({ model: value }, { model: toOptionalText(value) });
                  }}
                />
              </FormField>
              <FormField
                id="estimate-trim"
                label={t('estimates.trim')}
                required
                error={requiredError('trim')}
              >
                <Select
                  id="estimate-trim"
                  value={trimValue}
                  placeholder={t('catalog.chooseTrim')}
                  hasError={isMissing('trim')}
                  onBlur={touch('trim')}
                  onChange={(event) => onChange({ trim: event.target.value }, { trim: toOptionalText(event.target.value) })}
                  options={trimOptions(form.trim).map((trim) => ({
                    value: trim,
                    label: (TRIMS as readonly string[]).includes(trim) ? t(`enums.trim.${trim}`) : trim,
                  }))}
                />
              </FormField>
              {numberField('modelYear', t('estimates.modelYear'), { required: true })}
              {numberField('odometerKm', t('estimates.odometerKm'), { required: true })}
              <FormField
                id="estimate-vin"
                label={t('estimates.vin')}
                hint={vinMatches.length === 0 ? t('estimates.vinHint', { max: VIN_MAX_LENGTH }) : undefined}
                required
                error={vinTooLong ? t('estimates.vinTooLong', { max: VIN_MAX_LENGTH }) : requiredError('vin')}
              >
                <Input
                  id="estimate-vin"
                  dir="ltr"
                  className="font-mono"
                  maxLength={VIN_MAX_LENGTH}
                  value={form.vin}
                  hasError={vinTooLong || isMissing('vin')}
                  onBlur={touch('vin')}
                  onChange={(event) => {
                    const accepted = vinSchema.safeParse(event.target.value).success;

                    onChange({ vin: event.target.value }, accepted ? { vin: toOptionalText(event.target.value) } : null);
                  }}
                />
              </FormField>
              {numberField('ownerCount', t('estimates.ownerCount'), { required: true })}

              <div className="space-y-2">
                {numberField('referencePrice', t('estimates.referencePrice'), {
                  hint:
                    templateReferencePrice === null
                      ? t('estimates.referencePriceHint')
                      : t('estimates.referencePriceTemplateHint', { amount: formatMoney(templateReferencePrice) }),
                })}
                {canUpdateTemplate || alsoUpdateTemplate ? (
                  <Checkbox
                    id="estimate-update-template-price"
                    label={t('estimates.alsoUpdateTemplatePrice', { template: detail.template?.name ?? '' })}
                    checked={alsoUpdateTemplate}
                    disabled={setReferencePrice.isPending || templateUpdate !== null}
                    onChange={(event) => {
                      setAlsoUpdateTemplate(event.target.checked);

                      if (event.target.checked) {
                        void sendReferencePriceToTemplate();
                      }
                    }}
                  />
                ) : null}
                {templateUpdate !== null ? (
                  <p
                    role="status"
                    className="rounded-md border border-[#15803d]/30 bg-[#15803d]/5 px-3 py-2 text-xs text-[#15803d]"
                  >
                    {t(templateUpdate.createdDraft ? 'estimates.templatePriceDraftCreated' : 'estimates.templatePriceDraftUpdated', {
                      template: templateUpdate.templateName,
                      version: templateUpdate.draftVersion,
                      amount: formatMoney(templateUpdate.referencePrice),
                    })}
                  </p>
                ) : null}
                {templateUpdateError !== null ? <p className="text-xs text-destructive">{templateUpdateError}</p> : null}
              </div>

              {numberField('registrationTransferFee', t('estimates.registrationTransferFee'), {
                hint:
                  templateFees === null
                    ? t('estimates.registrationTransferFeeHint')
                    : t('estimates.registrationTransferFeeTemplateHint', { amount: formatMoney(templateFees, { exact: true }) }),
              })}

              <FormField
                id="estimate-fuel"
                label={t('estimates.fuel')}
              >
                <Select
                  id="estimate-fuel"
                  value={form.fuel}
                  onChange={(event) => onChange({ fuel: event.target.value as EstimatorForm['fuel'] }, { fuel: event.target.value as never })}
                  options={FUELS.map((fuel) => ({ value: fuel, label: t(`enums.fuel.${fuel}`) }))}
                />
              </FormField>
              <FormField
                id="estimate-registration-expiry"
                label={t('estimates.registrationExpiry')}
              >
                <Input
                  id="estimate-registration-expiry"
                  type="date"
                  dir="ltr"
                  value={form.registrationExpiry}
                  onChange={(event) =>
                    onChange({ registrationExpiry: event.target.value }, { registrationExpiry: toOptionalText(event.target.value) })
                  }
                />
              </FormField>
              <FormField
                id="estimate-origin"
                label={t('estimates.origin')}
              >
                <Select
                  id="estimate-origin"
                  value={form.origin}
                  placeholder={t('common.notRecorded')}
                  onChange={(event) =>
                    onChange({ origin: event.target.value as EstimatorForm['origin'] }, { origin: (event.target.value || null) as never })
                  }
                  options={ORIGINS.map((origin) => ({ value: origin, label: t(`enums.origin.${origin}`) }))}
                />
              </FormField>
              <FormField
                id="estimate-usage"
                label={t('estimates.usage')}
              >
                <Select
                  id="estimate-usage"
                  value={form.usage}
                  placeholder={t('common.notRecorded')}
                  onChange={(event) =>
                    onChange({ usage: event.target.value as EstimatorForm['usage'] }, { usage: (event.target.value || null) as never })
                  }
                  options={USAGES.map((usage) => ({ value: usage, label: t(`enums.usage.${usage}`) }))}
                />
              </FormField>
              <FormField
                id="estimate-service-history"
                label={t('estimates.serviceHistory')}
              >
                <Select
                  id="estimate-service-history"
                  value={form.serviceHistory}
                  onChange={(event) =>
                    onChange(
                      { serviceHistory: event.target.value as EstimatorForm['serviceHistory'] },
                      { serviceHistory: event.target.value as never },
                    )
                  }
                  options={SERVICE_HISTORIES.map((history) => ({ value: history, label: t(`enums.serviceHistory.${history}`) }))}
                />
              </FormField>
              {triStateField('faceliftApplies', t('estimates.faceliftApplies'), t('estimates.faceliftHint'))}
              {triStateField('customsApplies', t('estimates.customsApplies'), t('estimates.customsHint'))}
            </div>
          </FormSection>

          {vinMatches.length > 0 ? (
            <p className="rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-2 text-sm text-[#a16207]">
              {t('estimates.vinAlreadyOpen', {
                count: vinMatches.length,
                estimates: vinMatches.map((match) => match.estimateNo).join(', '),
              })}
            </p>
          ) : null}
        </div>

        <aside className="h-fit space-y-3 rounded-lg border border-border bg-card p-5 lg:sticky lg:top-6">
          <h2 className="text-sm font-semibold text-card-foreground">{t('estimates.estimateFirstTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('estimates.estimateFirstBody')}</p>
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">{t('estimates.estimateFirstNotice')}</p>
          <p className="text-xs text-muted-foreground">
            <span
              aria-hidden
              className="text-destructive"
            >
              *
            </span>{' '}
            {t('estimates.requiredLegend')}
          </p>
        </aside>
      </div>

      <QuickRangeCard
        quickRange={local?.quickRange ?? null}
        odometerKm={local?.valuation.bundle.facts.odometerKm ?? null}
        selected={detail.quickCategory}
        hasTemplate={templateValues !== null}
        onSelect={onSelectScenario}
      />
    </div>
  );
};
